import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Settings, MapPin, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminServices() {
  const [settings, setSettings] = useState({
    gabarit_price: 150,
    delivery_base_price: 100,
    installation_base_price: 200,
    distance_surcharge_per_km: 2,
    floor_surcharge: 100,
    floor_nous_consulter_threshold: 5,
    max_free_distance_km: 30
  });
  const [installationRules, setInstallationRules] = useState({
    gabarit_price: 150,
    workshop_address: "6 Rue de la porte écluse, 91270 Vigneux-sur-Seine",
    rules: [
      { id: "rule-1", zone_name: "Paris et 10km", min_distance_km: 0, max_distance_km: 10, price: 1100, is_nous_consulter: false, order: 1 },
      { id: "rule-2", zone_name: "Moins de 80km", min_distance_km: 0, max_distance_km: 80, price: 900, is_nous_consulter: false, order: 2 },
      { id: "rule-3", zone_name: "Plus de 80km", min_distance_km: 80, max_distance_km: -1, price: 0, is_nous_consulter: true, order: 3 }
    ]
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchInstallationRules();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/services`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchInstallationRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/installation-rules`);
      setInstallationRules(response.data);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings/services`, settings);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRules = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/installation-rules`, installationRules);
      toast.success('Règles de tarification enregistrées');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const addRule = () => {
    const newRule = {
      id: `rule-${Date.now()}`,
      zone_name: "Nouvelle zone",
      min_distance_km: 0,
      max_distance_km: 100,
      price: 0,
      is_nous_consulter: false,
      order: installationRules.rules.length + 1
    };
    setInstallationRules({
      ...installationRules,
      rules: [...installationRules.rules, newRule]
    });
  };

  const removeRule = (id) => {
    setInstallationRules({
      ...installationRules,
      rules: installationRules.rules.filter(r => r.id !== id)
    });
  };

  const updateRule = (id, field, value) => {
    setInstallationRules({
      ...installationRules,
      rules: installationRules.rules.map(r => 
        r.id === id ? { ...r, [field]: value } : r
      )
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-services">
      <div>
        <h1 className="text-2xl font-bold text-white">Paramètres des services</h1>
        <p className="text-white/50 mt-1">Configurez les tarifs des services et les règles de distance</p>
      </div>

      {/* Section 1: Prix de base des services */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmitSettings}
        className="card-dashboard space-y-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-[#D4AF37]" />
          <h2 className="text-lg font-semibold text-white">Prix de base (ancien système)</h2>
        </div>
        
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-white/60 text-sm mb-2 block">Gabarit (€)</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={settings.gabarit_price}
              onChange={(e) => setSettings({...settings, gabarit_price: parseFloat(e.target.value) || 0})}
              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
              data-testid="gabarit-price"
            />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-2 block">Livraison (€)</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={settings.delivery_base_price}
              onChange={(e) => setSettings({...settings, delivery_base_price: parseFloat(e.target.value) || 0})}
              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
              data-testid="delivery-base-price"
            />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-2 block">Pose (€)</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={settings.installation_base_price}
              onChange={(e) => setSettings({...settings, installation_base_price: parseFloat(e.target.value) || 0})}
              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
              data-testid="installation-base-price"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="btn-secondary flex items-center gap-2"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4" />
          Enregistrer
        </button>
      </motion.form>

      {/* Section 1bis: Supplément étage */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        onSubmit={handleSubmitSettings}
        className="card-dashboard space-y-6"
        data-testid="floor-settings-section"
      >
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-[#D4AF37]" />
          <h2 className="text-lg font-semibold text-white">Supplément étage</h2>
        </div>
        <p className="text-white/50 text-sm -mt-2">
          Un supplément forfaitaire est appliqué automatiquement par étage sur le forfait pose.
          Au-delà d'un seuil, le devis bascule en « Nous consulter ».
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-white/60 text-sm mb-2 block">
              Prix par étage (€ HT)
            </label>
            <Input
              type="number"
              min="0"
              step="1"
              value={settings.floor_surcharge ?? 100}
              onChange={(e) => setSettings({ ...settings, floor_surcharge: parseFloat(e.target.value) || 0 })}
              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
              data-testid="floor-surcharge-input"
            />
            <p className="text-white/40 text-xs mt-1">
              Ex. 100 € → étage 2 = +200 €, étage 3 = +300 €…
            </p>
          </div>
          <div>
            <label className="text-white/60 text-sm mb-2 block">
              Limite étage avant « Nous consulter »
            </label>
            <Input
              type="number"
              min="1"
              step="1"
              value={settings.floor_nous_consulter_threshold ?? 5}
              onChange={(e) => setSettings({ ...settings, floor_nous_consulter_threshold: parseInt(e.target.value) || 1 })}
              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
              data-testid="floor-threshold-input"
            />
            <p className="text-white/40 text-xs mt-1">
              À partir de cet étage, le prix bascule en « Nous consulter ».
            </p>
          </div>
        </div>

        <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-3 text-xs text-white/60">
          ℹ️ L'ascenseur est purement informatif : le supplément étage s'applique dans tous les cas.
        </div>

        <button
          type="submit"
          disabled={saving}
          className="btn-secondary flex items-center gap-2"
          data-testid="save-floor-settings-btn"
        >
          <Save className="w-4 h-4" />
          Enregistrer
        </button>
      </motion.form>

      {/* Section 2: Règles de tarification par distance */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        onSubmit={handleSubmitRules}
        className="card-dashboard space-y-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-[#D4AF37]" />
          <h2 className="text-lg font-semibold text-white">Règles de tarification par zone</h2>
        </div>

        {/* Prix du gabarit */}
        <div className="pb-4 border-b border-white/10">
          <label className="text-white/60 text-sm mb-2 block">Prix prise de gabarit (inclus dans le forfait)</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={installationRules.gabarit_price || 150}
            onChange={(e) => setInstallationRules({...installationRules, gabarit_price: parseFloat(e.target.value) || 0})}
            className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37] max-w-xs"
            data-testid="global-gabarit-price"
          />
          <p className="text-white/40 text-xs mt-1">Ce prix sera ajouté au forfait de pose</p>
        </div>

        {/* Adresse de l'atelier */}
        <div className="pb-4 border-b border-white/10">
          <label className="text-white/60 text-sm mb-2 block">Adresse de l'atelier (point de référence)</label>
          <Input
            value={installationRules.workshop_address || ""}
            onChange={(e) => setInstallationRules({...installationRules, workshop_address: e.target.value})}
            className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
            placeholder="Adresse complète de l'atelier"
            data-testid="workshop-address"
          />
        </div>

        {/* Règles de zone */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-white font-medium">Zones de tarification</label>
            <button
              type="button"
              onClick={addRule}
              className="text-[#D4AF37] text-sm flex items-center gap-1 hover:underline"
            >
              <Plus className="w-4 h-4" />
              Ajouter une zone
            </button>
          </div>

          {installationRules.rules?.map((rule, index) => (
            <div key={rule.id} className="p-4 bg-[#0A0A0A] border border-white/10 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[#D4AF37] text-sm">Zone {index + 1}</span>
                {installationRules.rules.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRule(rule.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Nom de la zone</label>
                  <Input
                    value={rule.zone_name}
                    onChange={(e) => updateRule(rule.id, 'zone_name', e.target.value)}
                    className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                    placeholder="Ex: Paris et 10km"
                    data-testid={`rule-name-${index}`}
                  />
                </div>
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Prix (€ HT)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={rule.price}
                    onChange={(e) => updateRule(rule.id, 'price', parseFloat(e.target.value) || 0)}
                    disabled={rule.is_nous_consulter}
                    className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37] disabled:opacity-50"
                    data-testid={`rule-price-${index}`}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Distance min (km)</label>
                  <Input
                    type="number"
                    min="0"
                    value={rule.min_distance_km}
                    onChange={(e) => updateRule(rule.id, 'min_distance_km', parseFloat(e.target.value) || 0)}
                    className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                    data-testid={`rule-min-${index}`}
                  />
                </div>
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Distance max (km, -1 = illimité)</label>
                  <Input
                    type="number"
                    value={rule.max_distance_km}
                    onChange={(e) => updateRule(rule.id, 'max_distance_km', parseFloat(e.target.value) || 0)}
                    className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                    data-testid={`rule-max-${index}`}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  checked={rule.is_nous_consulter}
                  onCheckedChange={(checked) => updateRule(rule.id, 'is_nous_consulter', checked)}
                  className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                  data-testid={`rule-consulter-${index}`}
                />
                <label className="text-white/60 text-sm">Afficher "Nous consulter" (pas de prix fixe)</label>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center gap-2"
            data-testid="save-rules-btn"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4" />
                Enregistrer les règles
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  );
}
